import { useCallback, useEffect, useState } from 'react';
import { Lock, Unlock, Plus, Save, ClipboardPaste, Undo2, Redo2, LogOut, Menu, SlidersHorizontal } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';
import { ScenarioSelector } from './ScenarioSelector';
import { cn } from '../lib/utils';
import { clearStoredToken } from '../lib/authToken';
import { DEAL_STAGES, getStageLabel, STAGE_TRIGGER_COLORS } from '../constants/dealStages';
import { NotificationBell } from './NotificationBell';
import * as api from '../lib/api';

const PRES_CURRENCIES = ['CHF', 'EUR', 'USD'];

export function TopBar({ 
  fxRates, 
  fxMode, 
  onLockToggle, 
  onSave,
  onNew,
  loading,
  activeTab,
  onTabChange,
  presentationCurrency,
  onPresentationCurrencyChange,
  vendorCurrency,
  onVendorCurrencyChange,
  hasDeal,
  stage,
  onStageChange,
  onPaste,
  onUndo,
  onRedo,
  canUndo,
  canRedo,
  scenarios,
  activeScenarioId,
  onScenarioActivate,
  onScenarioCreate,
  onScenarioDuplicate,
  onScenarioDelete,
  onScenarioRename,
  dealWorkspaceTab = 'deal',
  onDealWorkspaceTabChange,
  onCloseDealWorkspace,
  showDealContext = false,
  onIssueToBexio,
  issueToBexioDisabled = false,
  onDownloadQuotePdf,
  downloadQuotePdfDisabled = false,
  onNotificationAction,
}) {
  const navigate = useNavigate();
  const isLocked = fxMode === 'locked';
  const isDealsTab = activeTab === 'deals';
  const isVendorsTab = activeTab === 'vendors';
  const isForecastTab = activeTab === 'forecast';
  const isMaintenanceTab = activeTab === 'maintenance';
  const isSettingsTab = activeTab === 'settings';
  const isDealWorkspace = isDealsTab && hasDeal && showDealContext;
  const stageColor = STAGE_TRIGGER_COLORS[stage] || STAGE_TRIGGER_COLORS.Demand;
  const [mobileDealControlsOpen, setMobileDealControlsOpen] = useState(false);
  const [notificationUnreadCount, setNotificationUnreadCount] = useState(0);

  const refreshUnreadCount = useCallback(async () => {
    try {
      const res = await api.getNotificationsUnreadCount();
      setNotificationUnreadCount(Number(res?.data?.unread_count ?? 0));
    } catch {
      // Non-fatal in top bar.
    }
  }, []);

  useEffect(() => {
    void refreshUnreadCount();
    const t = window.setInterval(() => void refreshUnreadCount(), 60000);
    return () => window.clearInterval(t);
  }, [refreshUnreadCount]);

  const handleSignOut = () => {
    clearStoredToken();
    navigate('/login', { replace: true });
  };

  return (
    <div className="shrink-0 border-b border-gray-200 bg-white pt-[env(safe-area-inset-top)]" data-testid="top-bar">
      <div className="flex flex-col gap-1.5 px-[max(0.65rem,env(safe-area-inset-left))] py-1.5 pr-[max(0.65rem,env(safe-area-inset-right))] lg:px-4" data-testid="meta-menu">
        <div className="flex items-center justify-between gap-2 sm:hidden">
          <div className="text-[12px] font-semibold text-gray-800">
            {isDealWorkspace ? 'Deal workspace' : (activeTab || 'deals').charAt(0).toUpperCase() + (activeTab || 'deals').slice(1)}
          </div>
          <div className="flex items-center gap-1.5">
            {isDealsTab && (
              <Button
                size="sm"
                onClick={onNew}
                disabled={loading}
                className="h-7 shrink-0 gap-1.5 bg-slate-800 px-2.5 text-[11px] text-white hover:bg-slate-700"
                data-testid="new-deal-btn-topbar-mobile"
              >
                <Plus className="h-3.5 w-3.5" />
                New
              </Button>
            )}
            <NotificationBell
              onNotificationAction={onNotificationAction}
              unreadCount={notificationUnreadCount}
              onUnreadCountChange={setNotificationUnreadCount}
            />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon" className="h-7 w-7" data-testid="mobile-main-menu-trigger">
                  <Menu className="h-3.5 w-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-44">
                <DropdownMenuItem onSelect={() => onTabChange('deals')}>Deals</DropdownMenuItem>
                <DropdownMenuItem onSelect={() => onTabChange('vendors')}>Vendors</DropdownMenuItem>
                <DropdownMenuItem onSelect={() => onTabChange('forecast')}>Forecast</DropdownMenuItem>
                <DropdownMenuItem onSelect={() => onTabChange('maintenance')}>Maintenance</DropdownMenuItem>
                <DropdownMenuItem onSelect={() => onTabChange('settings')}>Settings</DropdownMenuItem>
                <DropdownMenuItem onSelect={handleSignOut}>Sign out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        <div className="hidden flex-wrap items-center justify-between gap-3 sm:flex">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <nav className="flex h-10 shrink-0 items-center gap-1 rounded-lg border border-gray-200 bg-gray-50 p-1" data-testid="main-tabs">
            <button
              onClick={() => onTabChange('deals')}
              className={cn(
                "min-h-7 min-w-[3.75rem] touch-manipulation rounded-md px-2.5 py-1.5 text-[12px] font-medium transition-colors sm:min-h-0 sm:min-w-0",
                isDealsTab
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-500 hover:text-gray-900 hover:bg-gray-100"
              )}
              data-testid="tab-deals"
            >
              Deals
            </button>
            <button
              onClick={() => onTabChange('vendors')}
              className={cn(
                "min-h-7 min-w-[3.75rem] touch-manipulation rounded-md px-2.5 py-1.5 text-[12px] font-medium transition-colors sm:min-h-0 sm:min-w-0",
                isVendorsTab
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-500 hover:text-gray-900 hover:bg-gray-100"
              )}
              data-testid="tab-vendors"
            >
              Vendors
            </button>
            <button
              onClick={() => onTabChange('forecast')}
              className={cn(
                "min-h-7 min-w-[3.75rem] touch-manipulation rounded-md px-2.5 py-1.5 text-[12px] font-medium transition-colors sm:min-h-0 sm:min-w-0",
                isForecastTab
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-500 hover:text-gray-900 hover:bg-gray-100"
              )}
              data-testid="tab-forecast"
            >
              Forecast
            </button>
            <button
              onClick={() => onTabChange('maintenance')}
              className={cn(
                "min-h-7 min-w-[3.75rem] touch-manipulation rounded-md px-2.5 py-1.5 text-[12px] font-medium transition-colors sm:min-h-0 sm:min-w-0",
                isMaintenanceTab
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-500 hover:text-gray-900 hover:bg-gray-100"
              )}
              data-testid="tab-maintenance"
            >
              Maintenance
            </button>
            <button
              onClick={() => onTabChange('settings')}
              className={cn(
                "min-h-7 min-w-[3.75rem] touch-manipulation rounded-md px-2.5 py-1.5 text-[12px] font-medium transition-colors sm:min-h-0 sm:min-w-0",
                isSettingsTab
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-500 hover:text-gray-900 hover:bg-gray-100"
              )}
              data-testid="tab-settings"
            >
              Settings
            </button>
          </nav>
            <Button
              size="sm"
              onClick={onNew}
              disabled={loading}
              className="h-7 shrink-0 gap-1.5 bg-slate-800 px-2.5 text-[11px] text-white hover:bg-slate-700"
              data-testid="new-deal-btn-topbar"
            >
              <Plus className="h-3.5 w-3.5" />
              New Deal
            </Button>
          </div>
          <div className="flex items-center gap-1.5">
            <NotificationBell
              onNotificationAction={onNotificationAction}
              unreadCount={notificationUnreadCount}
              onUnreadCountChange={setNotificationUnreadCount}
            />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleSignOut}
              className="h-7 shrink-0 gap-1 text-[11px] text-gray-500 hover:text-gray-800"
              data-testid="sign-out-btn"
            >
              <LogOut className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Sign out</span>
            </Button>
          </div>
        </div>

        {isDealWorkspace && (
          <div className="lg:hidden rounded-lg border border-gray-200 bg-gray-50/80 px-2 py-1.5" data-testid="deal-context-menu-mobile">
            <div className="flex items-center gap-1 overflow-x-auto pb-0.5">
              <button
                className={cn(
                  "rounded-md px-2 py-1 text-[11px] whitespace-nowrap",
                  dealWorkspaceTab === 'deal' ? 'bg-slate-800 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200',
                )}
                type="button"
                onClick={() => onDealWorkspaceTabChange?.('deal')}
              >
                Deal / Line Items
              </button>
              <button
                className={cn(
                  "rounded-md px-2 py-1 text-[11px] whitespace-nowrap",
                  dealWorkspaceTab === 'invoices' ? 'bg-slate-800 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200',
                )}
                type="button"
                onClick={() => onDealWorkspaceTabChange?.('invoices')}
              >
                Invoices & Payments
              </button>
              <button
                className={cn(
                  "rounded-md px-2 py-1 text-[11px] whitespace-nowrap",
                  dealWorkspaceTab === 'vendor-payments' ? 'bg-slate-800 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200',
                )}
                type="button"
                onClick={() => onDealWorkspaceTabChange?.('vendor-payments')}
              >
                Vendor Payments
              </button>
              <button
                className={cn(
                  "rounded-md px-2 py-1 text-[11px] whitespace-nowrap",
                  dealWorkspaceTab === 'calendar' ? 'bg-slate-800 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200',
                )}
                type="button"
                onClick={() => onDealWorkspaceTabChange?.('calendar')}
              >
                Calendar
              </button>
              <button
                className={cn(
                  "rounded-md px-2 py-1 text-[11px] whitespace-nowrap",
                  dealWorkspaceTab === 'notes' ? 'bg-slate-800 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200',
                )}
                type="button"
                onClick={() => onDealWorkspaceTabChange?.('notes')}
              >
                Notes
              </button>
              <button
                className={cn(
                  "rounded-md px-2 py-1 text-[11px] whitespace-nowrap",
                  dealWorkspaceTab === 'master-price-list' ? 'bg-slate-800 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200',
                )}
                type="button"
                onClick={() => onDealWorkspaceTabChange?.('master-price-list')}
                data-testid="workspace-tab-master-price-list-mobile"
              >
                Master Price List
              </button>
              <Button
                variant="ghost"
                size="icon"
                className="ml-1 h-6 w-6 shrink-0 text-gray-500"
                onClick={() => setMobileDealControlsOpen((v) => !v)}
                data-testid="mobile-deal-controls-toggle"
              >
                <SlidersHorizontal className="h-3.5 w-3.5" />
              </Button>
            </div>
            {mobileDealControlsOpen && (
              <div className="mt-1.5 grid grid-cols-2 gap-1.5">
                <Button variant="outline" size="sm" onClick={onPaste} disabled={loading || !hasDeal} className="h-6 justify-start gap-1 px-2 text-[10px]">
                  <ClipboardPaste className="h-3 w-3" /> Paste
                </Button>
                <Button variant="outline" size="sm" onClick={onSave} disabled={loading || !hasDeal} className="h-6 justify-start gap-1 px-2 text-[10px]">
                  <Save className="h-3 w-3" /> Save
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onDownloadQuotePdf}
                  disabled={loading || downloadQuotePdfDisabled}
                  className="col-span-2 h-6 justify-start px-2 text-[10px]"
                  data-testid="download-quote-pdf-btn-mobile"
                >
                  Download quote PDF
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onIssueToBexio}
                  disabled={loading || issueToBexioDisabled}
                  className="col-span-2 h-6 justify-start px-2 text-[10px]"
                  data-testid="issue-to-bexio-btn-mobile"
                >
                  Issue to Bexio
                </Button>
                <Button variant="outline" size="sm" onClick={onCloseDealWorkspace} className="h-6 justify-start px-2 text-[10px]">
                  Close
                </Button>
                <div className="flex items-center gap-0.5">
                  <Button variant="ghost" size="icon" onClick={onUndo} disabled={loading || !canUndo} className="h-6 w-6 text-gray-400">
                    <Undo2 className="h-3 w-3" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={onRedo} disabled={loading || !canRedo} className="h-6 w-6 text-gray-400">
                    <Redo2 className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {isDealWorkspace && (
          <div className="hidden min-w-0 flex-col gap-1 rounded-xl border border-gray-200 bg-gray-50/80 px-2 py-1 sm:px-2 lg:grid lg:grid-cols-[1fr_auto_auto] lg:items-center lg:gap-1" data-testid="deal-context-menu">
            <div className="flex min-w-0 flex-wrap items-center gap-1.5 lg:flex-nowrap lg:gap-1">
              <div className="max-w-full flex-1 lg:flex-none">
                <div className="flex flex-wrap items-center gap-1 pr-1 lg:flex-nowrap">
                <button
                  className={cn(
                    "rounded-md px-2 py-1 text-[11px] whitespace-nowrap",
                    dealWorkspaceTab === 'deal' ? 'bg-slate-800 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200',
                  )}
                  type="button"
                  onClick={() => onDealWorkspaceTabChange?.('deal')}
                  data-testid="workspace-tab-deal"
                >
                  Deal / Line Items
                </button>
                <button
                  className={cn(
                    "rounded-md px-2 py-1 text-[11px] whitespace-nowrap",
                    dealWorkspaceTab === 'invoices' ? 'bg-slate-800 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200',
                  )}
                  type="button"
                  onClick={() => onDealWorkspaceTabChange?.('invoices')}
                  data-testid="workspace-tab-invoices"
                >
                  Invoices & Payments
                </button>
                <button
                  className={cn(
                    "rounded-md px-2 py-1 text-[11px] whitespace-nowrap",
                    dealWorkspaceTab === 'vendor-payments' ? 'bg-slate-800 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200',
                  )}
                  type="button"
                  onClick={() => onDealWorkspaceTabChange?.('vendor-payments')}
                  data-testid="workspace-tab-vendor-payments"
                >
                  Vendor Payments
                </button>
                <button
                  className={cn(
                    "rounded-md px-2 py-1 text-[11px] whitespace-nowrap",
                    dealWorkspaceTab === 'calendar' ? 'bg-slate-800 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200',
                  )}
                  type="button"
                  onClick={() => onDealWorkspaceTabChange?.('calendar')}
                  data-testid="workspace-tab-calendar"
                >
                  Calendar
                </button>
                <button
                  className={cn(
                    "rounded-md px-2 py-1 text-[11px] whitespace-nowrap",
                    dealWorkspaceTab === 'notes' ? 'bg-slate-800 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200',
                  )}
                  type="button"
                  onClick={() => onDealWorkspaceTabChange?.('notes')}
                  data-testid="workspace-tab-notes"
                >
                  Notes
                </button>
                <button
                  className={cn(
                    "rounded-md px-2 py-1 text-[11px] whitespace-nowrap",
                    dealWorkspaceTab === 'master-price-list' ? 'bg-slate-800 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200',
                  )}
                  type="button"
                  onClick={() => onDealWorkspaceTabChange?.('master-price-list')}
                  data-testid="workspace-tab-master-price-list"
                >
                  Master Price List
                </button>
                </div>
              </div>
              <div className="flex items-center gap-1 lg:flex-nowrap">
                <div className="flex items-center gap-0.5" data-testid="undo-redo-group">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={onUndo}
                    disabled={loading || !canUndo}
                    className="h-6 w-6 text-gray-400 hover:text-gray-700"
                    title="Undo (Ctrl+Z)"
                    data-testid="undo-btn"
                  >
                    <Undo2 className="h-3 w-3" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={onRedo}
                    disabled={loading || !canRedo}
                    className="h-6 w-6 text-gray-400 hover:text-gray-700"
                    title="Redo (Ctrl+Y)"
                    data-testid="redo-btn"
                  >
                    <Redo2 className="h-3 w-3" />
                  </Button>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onPaste}
                  disabled={loading || !hasDeal}
                  title="Paste rows"
                  aria-label="Paste rows"
                  className="h-6 shrink-0 gap-1 px-2 text-[10px]"
                  data-testid="paste-rows-btn"
                >
                  <ClipboardPaste className="h-3 w-3" />
                  Paste
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onSave}
                  disabled={loading || !hasDeal}
                  className="h-6 shrink-0 gap-1 px-2 text-[10px]"
                  data-testid="save-deal-btn"
                >
                  <Save className="h-3 w-3" />
                  Save
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onDownloadQuotePdf}
                  disabled={loading || downloadQuotePdfDisabled}
                  className="h-6 shrink-0 px-2 text-[10px]"
                  data-testid="download-quote-pdf-btn"
                >
                  Download quote PDF
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onIssueToBexio}
                  disabled={loading || issueToBexioDisabled}
                  className="h-6 shrink-0 px-2 text-[10px]"
                  data-testid="issue-to-bexio-btn"
                >
                  Issue to Bexio
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onCloseDealWorkspace}
                  className="h-6 shrink-0 px-2 text-[10px]"
                  data-testid="close-deal-workspace-btn"
                >
                  Close
                </Button>
              </div>
            </div>

            <div className="flex min-w-0 flex-wrap items-center gap-1 lg:justify-center lg:flex-nowrap">
              <div className={cn("flex shrink-0 items-center rounded-md border px-2 py-1", stageColor)} data-testid="deal-stage-selector">
                <Select value={stage || 'Demand'} onValueChange={onStageChange}>
                  <SelectTrigger className="h-5 w-auto min-w-[72px] gap-1 border-0 bg-transparent p-0 pr-1 text-[11px] font-semibold focus:ring-0" data-testid="deal-stage-trigger">
                    <SelectValue>{getStageLabel(stage || 'Demand')}</SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {DEAL_STAGES.map(s => (
                      <SelectItem key={s.value} value={s.value} data-testid={`stage-option-${s.value}`}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <ScenarioSelector
                scenarios={scenarios || []}
                activeScenarioId={activeScenarioId}
                onActivate={onScenarioActivate}
                onCreate={onScenarioCreate}
                onDuplicate={onScenarioDuplicate}
                onDelete={onScenarioDelete}
                onRename={onScenarioRename}
                loading={loading}
              />
            </div>

            <div className="flex min-w-0 flex-wrap items-center gap-1 lg:ml-auto lg:flex-nowrap">
              <div className="flex shrink-0 items-center gap-1 rounded-md border border-blue-100 bg-blue-50 px-1.5 py-1" data-testid="pres-currency-selector">
                <span className="whitespace-nowrap text-[11px] font-semibold text-blue-500">
                  Client:
                </span>
                <Select
                  value={presentationCurrency}
                  onValueChange={onPresentationCurrencyChange}
                >
                  <SelectTrigger className="h-5 w-12 border-0 bg-transparent p-0 text-[10px] font-mono font-semibold focus:ring-0" data-testid="pres-currency-trigger">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PRES_CURRENCIES.map(c => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex shrink-0 items-center gap-1 rounded-md border border-orange-100 bg-orange-50 px-1.5 py-1" data-testid="vendor-currency-selector">
                <span className="whitespace-nowrap text-[11px] font-semibold text-orange-500">
                  Vendor:
                </span>
                <Select
                  value={vendorCurrency}
                  onValueChange={onVendorCurrencyChange}
                >
                  <SelectTrigger className="h-5 w-12 border-0 bg-transparent p-0 text-[10px] font-mono font-semibold focus:ring-0" data-testid="vendor-currency-trigger">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PRES_CURRENCIES.map(c => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex shrink-0 items-center gap-1.5 rounded-md border border-gray-100 bg-white px-1.5 py-1 font-mono text-[10px] tabular-nums" data-testid="fx-rates-display">
                <div className="flex items-center gap-1.5">
                  <span className="font-sans text-gray-400">EUR</span>
                  <span className="font-semibold text-gray-800" data-testid="fx-rate-eur">
                    {fxRates?.EUR_CHF?.toFixed(4) || '---'}
                  </span>
                </div>
                <div className="h-3 w-px bg-gray-200" />
                <div className="flex items-center gap-1.5">
                  <span className="font-sans text-gray-400">USD</span>
                  <span className="font-semibold text-gray-800" data-testid="fx-rate-usd">
                    {fxRates?.USD_CHF?.toFixed(4) || '---'}
                  </span>
                </div>
              </div>

              <Button
                variant="outline"
                size="sm"
                onClick={onLockToggle}
                disabled={loading}
                title={isLocked ? 'FX locked to snapshot' : 'FX live from market'}
                aria-label={isLocked ? 'FX locked' : 'FX live'}
                className="h-6 shrink-0 gap-1 px-2 text-[10px]"
                data-testid="fx-lock-toggle"
              >
                {isLocked ? (
                  <>
                    <Lock className="h-3.5 w-3.5" />
                    <span className="hidden sm:inline">Locked</span>
                  </>
                ) : (
                  <>
                    <Unlock className="h-3.5 w-3.5" />
                    <span className="hidden sm:inline">Live</span>
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
